import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CounselorResponse, counselorsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { CounselorPersonalDataFormValues } from "../CounselorPersonalData.types";
import { counselorPersonalDataValidationSchema } from "../CounselorPersonalData.utils";

export function useCounselorPersonalData(counselor: CounselorResponse) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const { teams } = useDropdownOptions();

  const saveMutation = useMutation({
    mutationFn: (values: CounselorPersonalDataFormValues) =>
      counselorsApi.saveCounselorPersonalData(
        {
          id: counselor.counselorId,
          saveCounselorPersonalDataRequest: {
            userCode: values.userCode.trim(),
            team: values.team,
            isTeamLeader: values.isTeamLeader,
            mobileNumber: values.mobileNumber.trim() || null,
            phoneExtension: values.phoneExtension.trim() || null,
          },
        },
        // Failures are reported below; without this the global middleware would also toast the backend's
        // untranslated message.
        withLocalErrorHandling
      ),
  });

  const validationSchema = useMemo(() => counselorPersonalDataValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<CounselorPersonalDataFormValues>({
    initialValues: {
      userCode: counselor.userCode,
      team: counselor.team ?? null,
      isTeamLeader: counselor.isTeamLeader,
      mobileNumber: counselor.mobileNumber ?? "",
      phoneExtension: counselor.phoneExtension ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await saveMutation.mutateAsync(values);
      } catch (error) {
        // A counselor code already used by another counselor or a volunteer is only known to the backend; it is
        // reported on the field so the form stays dirty and the user can pick another one.
        if ((await getApiErrorCode(error)) === ApiErrorCode.CounselorUserCodeTaken) {
          setFieldError("userCode", t("counselors.personalData.validation.userCodeTaken"));
        } else {
          showError(t("counselors.personalData.saveFailed"));
        }
        return;
      }

      showSuccess(t("counselors.personalData.saveSuccess"));
      // The edited counselor may be the signed-in user, whose own Kürzel (avatar, "Meine Kund:innen") comes
      // from the current user.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.CurrentUser] });
      // The detail page reads this counselor from the list cache; awaiting the refetch keeps the form
      // in its submitting state until the saved values (and the new audit line) have arrived.
      await queryClient.invalidateQueries({ queryKey: [QueryKey.Counselors] });
    },
  });

  return { formik, teamOptions: teams };
}
